import React from 'react';
import { 
  Scan, 
  Printer, 
  Timer, 
  Minimize2,
  Heart,
  Sparkles,
  CheckCircle2,
  Target,
  Shield,
  Microscope,
  Stethoscope,
  User
} from 'lucide-react';
import './Dto43e.css';

const Dto43e: React.FC = () => {
  return (
    <div className="dto43e">
      {/* Hero Section */}
      <div className="hero">
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <h1>Minimally Invasive Dental Implants</h1>
          <p>Advanced Technology for Faster Recovery and Better Results</p>
        </div>
      </div>

      {/* Introduction Section */}
      <section className="intro-section container">
        <div className="intro-content">
          <div className="intro-text">
            <h2>What are Minimally Invasive Dental Implants?</h2>
            <p>
              Experience dental implant surgery with the smallest possible wound area 
              using cutting-edge technology and expert techniques. Our approach eliminates 
              the need for traditional gingival flap surgery, resulting in shorter 
              operation times, minimal discomfort, and faster recovery.
            </p>
          </div>
          <div className="intro-image">
            <img 
              src="https://images.unsplash.com/photo-1579684385127-1ef15d508118?auto=format&fit=crop&q=80&w=1500"
              alt="Advanced Dental Technology"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* Core Technologies */}
      <section className="technologies-section">
        <div className="container">
          <h2 className="section-title">Advanced Technologies</h2>
          <div className="tech-grid">
            <div className="tech-card">
              <Scan className="tech-icon" />
              <h3>3D CT Scanning</h3>
              <p>
                Precise 3D imaging of bone structure for accurate implant placement 
                and treatment planning.
              </p>
              <ul className="tech-features">
                <li>Accurate bone measurement</li>
                <li>Detailed structural analysis</li>
                <li>Precise placement planning</li>
              </ul>
            </div>

            <div className="tech-card">
              <Printer className="tech-icon" />
              <h3>Custom Surgical Guides</h3>
              <p>
                3D-printed surgical guides created from combined CT and oral scan data 
                for precise implant positioning.
              </p>
              <ul className="tech-features">
                <li>Patient-specific design</li>
                <li>Enhanced accuracy</li>
                <li>Reduced procedure time</li>
              </ul>
            </div>

            <div className="tech-card">
              <Timer className="tech-icon" />
              <h3>Immediate Placement</h3>
              <p>
                Same-day extraction and implant placement utilizing existing socket 
                for minimal intervention.
              </p>
              <ul className="tech-features">
                <li>Reduced treatment time</li>
                <li>Minimal tissue trauma</li>
                <li>Faster healing process</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Advantages Section */}
      <section className="advantages-section container">
        <h2 className="section-title">Key Benefits</h2>
        <div className="advantages-content">
          <div className="advantages-grid">
            <div className="advantage-card">
              <Minimize2 className="advantage-icon" />
              <h3>Minimal Wound Size</h3>
              <p>1/3 to 1/4 smaller than traditional implant surgery</p>
            </div>

            <div className="advantage-card">
              <Heart className="advantage-icon" />
              <h3>Reduced Discomfort</h3>
              <p>Significantly less post-operative pain and swelling</p>
            </div>

            <div className="advantage-card">
              <Sparkles className="advantage-icon" />
              <h3>Quick Recovery</h3>
              <p>Faster healing with minimal risk of infection</p>
            </div>
          </div>
          <div className="advantages-image">
            <img 
              src="https://images.unsplash.com/photo-1606811841689-23dfddce3e95?auto=format&fit=crop&q=80&w=1500"
              alt="Dental Treatment"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* Treatment Process */}
      <section className="process-section">
        <div className="container">
          <h2 className="section-title">Treatment Process</h2>
          <div className="process-timeline">
            <div className="timeline-item">
              <div className="timeline-icon">
                <Stethoscope />
              </div>
              <div className="timeline-content">
                <h3>Initial Planning</h3>
                <p>
                  Comprehensive evaluation using 3D CT scans and digital planning 
                  software for precise treatment design.
                </p>
              </div>
            </div>

            <div className="timeline-item">
              <div className="timeline-icon">
                <Target />
              </div>
              <div className="timeline-content">
                <h3>Guided Surgery</h3>
                <p>
                  Minimally invasive implant placement using custom surgical guides 
                  for optimal positioning.
                </p>
              </div>
            </div>

            <div className="timeline-item">
              <div className="timeline-icon">
                <Shield />
              </div>
              <div className="timeline-content">
                <h3>Healing Period</h3>
                <p>
                  Monitored healing process with shorter recovery time compared to 
                  traditional methods.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Case Study */}
      <section className="case-study-section container">
        <h2 className="section-title">Success Story</h2>
        <div className="case-study">
          <div className="case-content">
            <div className="case-text">
              <h3>Anterior Teeth Restoration</h3>
              <p>
                Immediate extraction and implant placement in the aesthetic zone, 
                showcasing minimal invasion and optimal aesthetic results.
              </p>
              <ul className="case-features">
                <li>
                  <CheckCircle2 className="feature-icon" />
                  <span>Small surgical wound</span>
                </li>
                <li>
                  <CheckCircle2 className="feature-icon" />
                  <span>Rapid recovery</span>
                </li>
                <li>
                  <CheckCircle2 className="feature-icon" />
                  <span>Natural aesthetics</span>
                </li>
              </ul>
            </div>
            <div className="case-image">
              <img 
                src="https://images.unsplash.com/photo-1581594693702-fbdc51b2763b?auto=format&fit=crop&q=80&w=1500"
                alt="Dental Implant Case"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Expert Section */}
      <section className="expert-section">
        <div className="container">
          <h2 className="section-title">Expert Care</h2>
          <div className="expert-grid">
            <div className="expert-card">
              <User className="expert-icon" />
              <h3>Experienced Specialists</h3>
              <p>
                Our team includes former lecturers from world-leading implant companies 
                with extensive experience in minimally invasive techniques.
              </p>
            </div>

            <div className="expert-card">
              <Microscope className="expert-icon" />
              <h3>Advanced Technology</h3>
              <p>
                State-of-the-art equipment and materials for precise, predictable 
                results.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Dto43e;